// Przewodnik „pierwsze kroki" dla organizacji demo — treść kroków, stan
// „widziane" i czysta geometria podświetlenia (spotlight + dymek).
// Czyste funkcje (storage/wymiary wstrzykiwane), żeby dało się je testować bez DOM.
// Renderuje components/DemoTour.js, uruchamiany globalnie w App.js dla user.isDemo.

const STORAGE_PREFIX = 'demoTourSeen:v1';

// Treść przewodnika. Kolejność = kolejność kroków.
// `route`   — dokąd przejść PRZED pokazaniem kroku (użytkownik widzi prawdziwy ekran w tle),
// `target`  — selektor elementu do podświetlenia; gdy go nie ma (schowany sidebar na
//             telefonie, pozycja tylko dla admina), krok pokazuje się na środku ekranu.
export const DEMO_TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Witaj w swoim demo',
    lead: 'To jest w pełni działająca wypożyczalnia - tylko Twoja i odizolowana od innych.',
    route: '/home',
    points: [
      'Wgraliśmy przykładowy sprzęt, klientów i rezerwacje, żebyś nie zaczynał od pustych ekranów.',
      'Za chwilę pokażę Ci na ekranie, co gdzie jest - będziemy przechodzić po kolejnych sekcjach.',
      'Klikaj śmiało: nic, co tu zrobisz, nie trafi do prawdziwych klientów.',
    ],
  },
  {
    id: 'menu',
    title: 'Menu - stąd wchodzisz wszędzie',
    lead: 'Podświetlone menu jest podzielone na cztery grupy.',
    target: '[data-tour="sidebar-nav"]',
    points: [
      'Główne - Strona główna, Nowa rezerwacja, Kalendarz.',
      'Zarządzanie - Oferty, Klienci, Magazyn, Płatności, Statystyki.',
      'Narzędzia - Konstruktor umowy, Konstruktor oferty, Płatności online, Faktury, Powiadomienia.',
      'Konto - Profil, czyli ustawienia firmy, cenniki i dane do dokumentów.',
    ],
  },
  {
    id: 'calendar',
    title: 'Kalendarz - Twój dzień na jednym ekranie',
    lead: 'Otworzyłem go w tle: każdy pasek to jedna rezerwacja.',
    route: '/calendar',
    target: '[data-tour="nav:/calendar"]',
    points: [
      'Sprawdzasz, co jest zajęte w danym terminie i kto dziś oddaje sprzęt.',
      'Klik w dzień pokazuje rezerwacje z tego dnia, klik w pasek - szczegóły i płatności.',
      'Z widoku rezerwacji wygenerujesz umowę, a przy zwrocie dodasz zdjęcia stanu sprzętu.',
    ],
  },
  {
    id: 'reservation',
    title: 'Nowa rezerwacja - tutaj powstaje sprzedaż',
    lead: 'Formularz w tle prowadzi Cię od terminu do gotowej rezerwacji.',
    route: '/reservation',
    target: '[data-tour="nav:/reservation"]',
    points: [
      'Wybierasz termin i sprzęt - system pokazuje tylko to, co w tych dniach wolne.',
      'Klienta wyszukujesz po mailu; nowego dodajesz bez wychodzenia z formularza.',
      'Cena liczy się sama z cennika, razem z rabatami i pakietami - możesz ją nadpisać.',
    ],
  },
  {
    id: 'inventory',
    title: 'Magazyn - cały Twój sprzęt',
    lead: 'To lista, z której korzystają rezerwacje i wyceny.',
    route: '/inventory',
    target: '[data-tour="nav:/inventory"]',
    points: [
      'Każda pozycja ma kategorię, status i cenę za dobę.',
      'Klik w sprzęt pokazuje jego kalendarz - kiedy jest zajęty i przez kogo.',
      'Ceny sezonowe i pakiety (np. sprzęt z akcesoriami we wspólnej cenie) ustawisz w Profilu.',
    ],
  },
  {
    id: 'documents',
    title: 'Faktury i dokumenty',
    lead: 'Oferta, faktura i płatność wychodzą z tej samej rezerwacji.',
    route: '/invoices',
    target: '[data-tour="nav:/invoices"]',
    points: [
      'Wystawiasz proformy, faktury VAT i korekty, z wysyłką do KSeF.',
      'W Ofertach wycenisz zapytanie i wyślesz klientowi PDF, a z oferty zrobisz rezerwację.',
      'W Płatnościach widzisz zaliczki i rozliczenia poszczególnych rezerwacji.',
    ],
  },
  {
    id: 'limits',
    title: 'Czym demo różni się od wersji pełnej',
    lead: 'W tym pasku masz licznik dni - i powrót do tego przewodnika.',
    route: '/home',
    target: '[data-tour="demo-banner"]',
    points: [
      'Maile do klientów (oferty, faktury, powiadomienia) nie wychodzą - akcja się wykona, wiadomość zostaje u nas.',
      'Płatności online są zablokowane, więc nie da się obciążyć niczyjej karty.',
      'Nie dodasz pracowników ani własnego serwera pocztowego - to ustawienia wersji pełnej.',
      'Przycisk „Przewodnik" w tym pasku otworzy ten spacer po aplikacji jeszcze raz.',
    ],
  },
];

export function demoTourStorageKey(userId) {
  return `${STORAGE_PREFIX}:${userId ?? 'anon'}`;
}

// Odczyt odporny na wyłączony/zapełniony localStorage — brak wpisu i błąd
// traktujemy tak samo: przewodnik pokaże się jeszcze raz, nic się nie psuje.
export function isDemoTourSeen(storage, userId) {
  try {
    return storage?.getItem(demoTourStorageKey(userId)) === '1';
  } catch (_) {
    return false;
  }
}

export function markDemoTourSeen(storage, userId) {
  try {
    storage?.setItem(demoTourStorageKey(userId), '1');
  } catch (_) {
    // brak miejsca / prywatny tryb — przewodnik po prostu pokaże się ponownie
  }
}

export function clampStepIndex(index, total = DEMO_TOUR_STEPS.length) {
  if (!Number.isFinite(index)) return 0;
  return Math.min(Math.max(Math.trunc(index), 0), Math.max(total - 1, 0));
}

export function formatStepCounter(index, total = DEMO_TOUR_STEPS.length) {
  return `Krok ${clampStepIndex(index, total) + 1} z ${total}`;
}

// Element nadaje się do podświetlenia tylko wtedy, gdy naprawdę widać go na ekranie.
// Zwinięty sidebar na telefonie ma rect o zerowej szerokości albo leży poza viewportem —
// wtedy krok spada na wariant „karta na środku", zamiast podświetlać pustkę.
export function isRectVisible(rect, viewport) {
  if (!rect || !viewport) return false;
  if (!(rect.width > 0) || !(rect.height > 0)) return false;
  return rect.right > 0 && rect.bottom > 0 && rect.left < viewport.width && rect.top < viewport.height;
}

// Ramka podświetlenia: prostokąt elementu powiększony o `padding` i przycięty do ekranu.
export function spotlightBox(rect, viewport, padding = 8) {
  if (!isRectVisible(rect, viewport)) return null;
  const left = Math.max(rect.left - padding, 0);
  const top = Math.max(rect.top - padding, 0);
  const right = Math.min(rect.right + padding, viewport.width);
  const bottom = Math.min(rect.bottom + padding, viewport.height);
  return { left, top, width: Math.max(right - left, 0), height: Math.max(bottom - top, 0) };
}

// Przybliżony rozmiar karty przewodnika (szerokość z TourOverlay, wysokość typowego kroku:
// tytuł, wstęp, trzy punkty, przyciski). Wystarczy do decyzji o przewinięciu, pozycję karty
// liczy potem placeTooltip z jej prawdziwych wymiarów.
export const TOUR_CARD_ESTIMATE = { width: 420, height: 400 };

// Czy element trzeba przewinąć, zanim go podświetlimy: gdy nie mieści się cały na ekranie
// albo nigdzie obok niego nie ma miejsca na kartę (wtedy karta stanęłaby na środku i go
// zasłoniła). Element schowany w poziomie (zwinięte menu na telefonie) zostaje w spokoju:
// przewijanie w pionie nic tu nie da, a w poziomie rozjechałoby układ.
export function needsScrollForRoom(rect, viewport, card = TOUR_CARD_ESTIMATE, gap = 16, margin = 12) {
  if (!rect || !viewport) return false;
  if (!(rect.width > 0) || !(rect.height > 0)) return false;
  if (rect.right <= 0 || rect.left >= viewport.width) return false;
  const caly = rect.top >= 0 && rect.bottom <= viewport.height;
  if (!caly) return true;
  const zapas = (rozmiar) => rozmiar + gap + margin;
  const obok = viewport.width - rect.right >= zapas(card.width) || rect.left >= zapas(card.width);
  const nadPod = viewport.height - rect.bottom >= zapas(card.height) || rect.top >= zapas(card.height);
  return !(obok || nadPod);
}

// Czy dwie ramki są tą samą ramką (z dokładnością do pół piksela; `null` = brak ramki).
// TourOverlay mierzy przy każdej zmianie strony i bez tego ustawiałby stan w kółko.
export function sameBox(a, b, tolerance = 0.5) {
  if (!a || !b) return a === b;
  return ['left', 'top', 'width', 'height'].every((k) => Math.abs(a[k] - b[k]) <= tolerance);
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

// Dymek stawiamy obok podświetlenia: najpierw z prawej, potem z lewej, niżej, wyżej.
// Gdy nigdzie się nie mieści (mały ekran, wielki dymek) — wracamy na środek ekranu.
export function placeTooltip({ target, tooltip, viewport, gap = 16, margin = 12 }) {
  if (!target || !tooltip || !viewport) return { placement: 'center' };

  const fitsRight = viewport.width - target.left - target.width >= tooltip.width + gap + margin;
  const fitsLeft = target.left >= tooltip.width + gap + margin;
  const fitsBelow = viewport.height - target.top - target.height >= tooltip.height + gap + margin;
  const fitsAbove = target.top >= tooltip.height + gap + margin;

  const maxLeft = viewport.width - tooltip.width - margin;
  const maxTop = viewport.height - tooltip.height - margin;

  if (fitsRight || fitsLeft) {
    const placement = fitsRight ? 'right' : 'left';
    const left = fitsRight ? target.left + target.width + gap : target.left - tooltip.width - gap;
    const top = target.top + target.height / 2 - tooltip.height / 2;
    return { placement, left: clamp(left, margin, maxLeft), top: clamp(top, margin, maxTop) };
  }

  if (fitsBelow || fitsAbove) {
    const placement = fitsBelow ? 'bottom' : 'top';
    const top = fitsBelow ? target.top + target.height + gap : target.top - tooltip.height - gap;
    const left = target.left + target.width / 2 - tooltip.width / 2;
    return { placement, left: clamp(left, margin, maxLeft), top: clamp(top, margin, maxTop) };
  }

  // Duży element (np. cała grupa kart w Profilu): obok się nie mieści, ale nad albo pod nim
  // zostaje trochę ekranu. Karta staje wtedy przy tej krawędzi ekranu, gdzie miejsca jest
  // więcej, i zasłania brzeg podświetlenia zamiast jego środka.
  const spaceBelow = viewport.height - target.top - target.height;
  const spaceAbove = target.top;
  if (Math.max(spaceBelow, spaceAbove) > 0 && maxTop >= margin) {
    const top = spaceBelow >= spaceAbove ? maxTop : margin;
    const left = target.left + target.width / 2 - tooltip.width / 2;
    return { placement: 'edge', left: clamp(left, margin, maxLeft), top };
  }

  return { placement: 'center' };
}
